<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>
          Private {{ $t('Requests') }}
          <span v-if="client">&ndash; {{ client.name }}</span>
          <span v-if="manager">&ndash; {{ manager.company }}</span>
          <help-link v-if="!$vlastMode" anchor="requests" />
        </h1>
      </div>
    </div>

    <div class="container-fluid">

      <div class="dashboard-results-details">
        <div class="w-full flex flex-wrap justify-between spacer-b-2">
          <div class="count"><span>{{ pagination.total }}</span> Private {{ $t('Requests') }}</div>
          <booking-list-sort
            :set-filter-mutation="setFilter"
            :filters="filters"
            :options="sortOptions"
          />
        </div>
      </div>

      <booking-list-filters
        v-if="$userIsAdmin || $userIsManager"
        :set-filter-mutation="setFilter"
        :filters="filters"
        :apply-filters="handleApplyFiltersCallback">
        <ladda-button
          slot="button"
          :click="handleApplyFiltersCallback"
          :disabled="$userIsInactiveManager"
          type="submit"
          class="btn btn-primary pull-right">
          Search
        </ladda-button>
      </booking-list-filters>

      <div class="dashboard-block">
        <ul class="nav nav-tabs" role="tablist">
          <li :class="{ 'active': status === 'active' }">
            <router-link :to="{ params: { status: 'active' }, query: $route.query }">{{ $userIsAdmin ? 'View All (Active)' : 'Active' }}</router-link>
          </li>
          <li v-if="$userIsAdmin" :class="{ 'active': status === 'event' }">
            <router-link :to="{ params: { status: 'event' }, query: $route.query }">In Person</router-link>
          </li>
          <li v-if="$userIsAdmin" :class="{ 'active': status === 'campaign' }">
            <router-link :to="{ params: { status: 'campaign' }, query: $route.query }">Online</router-link>
          </li>
          <li v-if="$route.params.clientId" :class="{ 'active': status === 'vetting' }">
            <router-link :to="{ params: { status: 'vetting' }, query: $route.query }">Vetting</router-link>
          </li>
          <li :class="{ 'active': status === 'archived' }">
            <router-link :to="{ params: { status: 'archived' }, query: $route.query }">Archived</router-link>
          </li>
        </ul>

        <pickstar-loading v-if="fetchingBookingRequests" class="m-t-lg"/>

        <template v-else>
          <no-results v-if="$userIsInactiveManager" class="text-center">
            <div>
              You will be able to view {{ $t('requests') }} once {{ clientName }} has approved your account. In the meantime,
              build out your <router-link :to="{ name: 'profile.show' }">profile</router-link> or start
              <router-link :to="{ name: 'talent.index' }">adding your {{ $starLabelPlural }} for review!</router-link>
            </div>
          </no-results>
          <no-results v-else-if="haveNoBookingRequests">
            <div>There are no {{ $t('booking requests') }}.</div>

            <button v-if="haveAppliedFilters" @click="handleResetFiltersCallback" class="btn btn-default btn-sm m-t-sm">Reset Filters</button>
          </no-results>

          <ul class="list-items">
            <request-list-item
              v-for="booking in requests"
              :key="booking.id"
              :booking="booking"/>
          </ul>

          <lazy-loading
            v-if="!$userIsInactiveManager"
            class="text-center"
            :loading="loading"
            :reached-bottom="handleGetNextBookingRequestsCallback"
            ref="lazyLoading">
            <pickstar-loading slot="loading"/>
            <span slot="finished"></span>
          </lazy-loading>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { appliesFilters, handleServerErrors, fetchResourceScope } from 'mixins'
import RequestListItem from './RequestListItem'
import BookingListFilters from 'components/booking/BookingListFilters'
import BookingListSort from 'components/booking/BookingListSort'
import LazyLoading from 'codium-lazy-loading'
import HelpLink from 'components/globals/HelpLink'

export default {
  mixins: [fetchResourceScope, appliesFilters, handleServerErrors],

  props: {
    status: {
      required: false,
      default: 'active'
    }
  },

  components: {
    RequestListItem,
    BookingListSort,
    BookingListFilters,
    LazyLoading,
    HelpLink
  },

  data () {
    return {
      fetchingBookingRequests: false
    }
  },

  computed: {
    ...mapState('booking/request', ['requests', 'pagination', 'filters']),
    ...mapGetters('booking/request', ['initialFiltersState']),
    ...mapState('app', ['loading']),

    haveNoBookingRequests () {
      return this.requests.length === 0
    },

    sortOptions () {
      let sortOptions = [
        {
          label: 'Soonest Event Date',
          sort: 'date',
          order: 'asc'
        },
        {
          label: 'Furthest Event Date',
          sort: 'date',
          order: 'desc'
        },
        {
          label: this.monetaryCurrency ? 'Lowest Price' : 'Lowest Points',
          sort: 'price',
          order: 'asc'
        },
        {
          label: this.monetaryCurrency ? 'Highest Price' : 'Highest Points',
          sort: 'price',
          order: 'desc'
        }
      ]
      if (this.$userIsAdmin) {
        sortOptions = sortOptions.concat([
          {
            label: 'Most Urgent',
            sort: 'urgent',
            order: 'asc'
          },
          {
            label: 'Most Recent Creation Date',
            sort: 'created_at',
            order: 'desc'
          },
          {
            label: 'Recently Contacted',
            sort: 'notes.created_at',
            order: 'desc'
          },
          {
            label: 'Least Recently Contacted',
            sort: 'notes.created_at',
            order: 'asc'
          },
          {
            label: 'Most Recently Vetted',
            sort: 'vetted_date',
            order: 'desc'
          },
          {
            label: 'Least Recently Vetted',
            sort: 'vetted_date',
            order: 'asc'
          }
        ])
      }
      if (this.$userIsAdmin && !this.$vlastMode) {
        sortOptions = sortOptions.concat([
          {
            label: 'Highest Probability',
            sort: 'probability',
            order: 'desc'
          },
          {
            label: 'Lowest Probability',
            sort: 'probability',
            order: 'asc'
          }
        ])
        sortOptions = sortOptions.concat([
          {
            label: 'Soonest Est Close Date',
            sort: 'est_close_date',
            order: 'asc'
          },
          {
            label: 'Furthest Est Close Date',
            sort: 'est_close_date',
            order: 'desc'
          }
        ])
      }
      return sortOptions
    }
  },

  watch: {
    '$route' () {
      if (!this.$userIsInactiveManager) {
        this.handleGetBookingRequestsCallback()
      }
    }
  },

  methods: {
    ...mapActions('booking/request', ['getBookingRequests', 'getNextBookingRequests', 'setFilter', 'resetFilters']),

    handleGetBookingRequestsCallback () {
      this.fetchingBookingRequests = true

      return this.getBookingRequests(this.queryParams)
        .catch(this.handleServerErrorsCallback)
        .finally(() => {
          this.fetchingBookingRequests = false

          this.$nextTick(() => {
            if (!this.$refs.lazyLoading) {
              return
            }

            this.$refs.lazyLoading.$emit('reset')
          })
        })
    },

    handleGetNextBookingRequestsCallback () {
      return this.getNextBookingRequests(this.queryParams)
        .catch(this.handleServerErrorsCallback)
    }
  }
}
</script>
